import React, { useState, useRef } from 'react';
import { Bug, X, Send, AlertTriangle, AlertCircle, Info, Camera, CheckCircle2, MessageSquare, Lightbulb, Grid } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { apiFetch } from '../services/cloudflareService';
import html2canvas from 'html2canvas';

interface BugReportWidgetProps {
    isDarkMode?: boolean;
    hospitalName?: string;
}

const SEVERITY_OPTIONS = [
    { value: 'low', label: 'Menor', icon: Info, color: 'text-blue-400', bg: 'bg-blue-500/10 border-blue-500/20', bgActive: 'bg-blue-500 text-white' },
    { value: 'medium', label: 'Molesto', icon: AlertCircle, color: 'text-amber-400', bg: 'bg-amber-500/10 border-amber-500/20', bgActive: 'bg-amber-500 text-white' },
    { value: 'high', label: 'Crítico', icon: AlertTriangle, color: 'text-red-400', bg: 'bg-red-500/10 border-red-500/20', bgActive: 'bg-red-500 text-white' },
];

const CATEGORY_OPTIONS = [
    { value: 'bug', label: 'Error / Bug', icon: Bug },
    { value: 'ux', label: 'Queja de UX', icon: MessageSquare },
    { value: 'feature_request', label: 'Mejora / Feature', icon: Lightbulb },
    { value: 'other', label: 'Otro', icon: Grid },
];

// Resolves a readable screen name from the current path
const getScreenName = (path: string) => {
    if (path.includes('/staff/dashboard')) return 'Dashboard de Enfermería';
    if (path.includes('/staff/room-control')) return 'Control de Quirófano';
    if (path.includes('/admin/hub')) {
        const search = window.location.search;
        if (search.includes('tab=ads')) return 'Panel Admin > Anuncios';
        if (search.includes('tab=calendar')) return 'Panel Admin > Calendario';
        if (search.includes('tab=analytics')) return 'Panel Admin > Analíticas Médicas';
        if (search.includes('tab=ad_analytics')) return 'Panel Admin > Métricas Marketing';
        if (search.includes('tab=feedback')) return 'Panel Admin > Satisfacción';
        if (search.includes('tab=monitors')) return 'Panel Admin > Monitores';
        if (search.includes('tab=accesos')) return 'Panel Admin > Accesos';
        if (search.includes('tab=integraciones')) return 'Panel Admin > Integraciones';
        if (search.includes('tab=settings')) return 'Panel Admin > Configuración';
        return 'Panel Admin > Resumen';
    }
    if (path.includes('/admin/dashboard')) return 'Panel Global (Cadenas)';
    if (path.includes('/login')) return 'Pantalla de Acceso';
    if (path.includes('/board')) return 'TV Board Público';
    return path;
};

const BugReportWidget: React.FC<BugReportWidgetProps> = ({ isDarkMode = false, hospitalName }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [severity, setSeverity] = useState('medium');
    const [category, setCategory] = useState('bug');
    const [isSending, setIsSending] = useState(false);
    const [sent, setSent] = useState(false);

    // Screenshot state
    const [isCapturing, setIsCapturing] = useState(false);
    const [screenshotUrl, setScreenshotUrl] = useState<string | null>(null);

    const handleCapture = async () => {
        setIsCapturing(true);
        try {
            // Hide widget during capture
            setIsOpen(false);
            // Wait for modal exit animation
            await new Promise(res => setTimeout(res, 300));
            
            const canvas = await html2canvas(document.body, {
                useCORS: true,
                scale: 1.5, // slightly reduced for smaller filesize, still legible
                ignoreElements: (el) => el.id === 'bug-report-widget' // fallback if hidden fails
            });
            
            // Re-open widget
            setIsOpen(true);
            
            // Convert to blob
            canvas.toBlob(async (blob) => {
                if (!blob) return;
                
                // We upload the screenshot immediately to R2 to get a URL
                const formData = new FormData();
                formData.append('file', blob, 'screenshot.jpg');
                
                try {
                    const res = await apiFetch('/api/bugs/screenshot', {
                        method: 'POST',
                        credentials: 'include',
                        body: formData
                    }, false); // don't stringify body
                    
                    if (res.ok) {
                        const data = await res.json();
                        setScreenshotUrl(data.screenshotUrl);
                    }
                } catch (e) {
                    console.error('Failed to upload screenshot:', e);
                } finally {
                    setIsCapturing(false);
                }
            }, 'image/jpeg', 0.8);
            
        } catch (e) {
            console.error('Capture failed:', e);
            setIsCapturing(false);
            setIsOpen(true);
        }
    };

    const handleSubmit = async () => {
        if (!title.trim() || title.trim().length < 3) return;
        setIsSending(true);
        try {
            await apiFetch('/api/bugs', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    title: title.trim(),
                    description: description.trim() || null,
                    severity,
                    category,
                    page: window.location.pathname,
                    screenName: getScreenName(window.location.pathname),
                    screenshotUrl,
                    userAgent: navigator.userAgent.slice(0, 400),
                }),
            });
            setSent(true);
            setTimeout(() => {
                setIsOpen(false);
                setSent(false);
                setTitle('');
                setDescription('');
                setSeverity('medium');
                setCategory('bug');
                setScreenshotUrl(null);
            }, 2000);
        } catch (e) {
            console.error('Bug report failed:', e);
        } finally {
            setIsSending(false);
        }
    };

    return (
        <div id="bug-report-widget">
            {/* Discrete footer link */}
            <button
                onClick={() => setIsOpen(true)}
                className={`inline-flex items-center gap-1.5 text-[11px] transition-all duration-200 group ${
                    isDarkMode
                        ? 'text-white/20 hover:text-white/50'
                        : 'text-slate-300 hover:text-slate-500'
                }`}
            >
                <Bug className="w-3 h-3 group-hover:scale-110 transition-transform" />
                <span>Reportar un problema</span>
            </button>

            {/* Modal Overlay */}
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
                        onClick={() => !isSending && setIsOpen(false)}
                    >
                        {/* Backdrop */}
                        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

                        {/* Modal */}
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95, y: 20 }}
                            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                            onClick={(e) => e.stopPropagation()}
                            className={`relative w-full max-w-md rounded-3xl overflow-hidden shadow-2xl border ${
                                isDarkMode
                                    ? 'bg-[#14161b] border-white/10'
                                    : 'bg-white border-slate-200'
                            } flex flex-col max-h-[90vh]`}
                        >
                            {/* Header */}
                            <div className={`shrink-0 px-6 pt-6 pb-4 flex items-center justify-between border-b ${
                                isDarkMode ? 'border-white/5' : 'border-slate-100'
                            }`}>
                                <div className="flex items-center gap-3">
                                    <div className={`w-10 h-10 rounded-2xl flex items-center justify-center ${
                                        isDarkMode ? 'bg-indigo-500/10' : 'bg-indigo-50'
                                    }`}>
                                        <Bug className="w-5 h-5 text-indigo-500" />
                                    </div>
                                    <div>
                                        <h3 className={`text-lg font-black tracking-tight ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                                            Buzón de Soporte
                                        </h3>
                                        <p className={`text-[10px] font-bold uppercase tracking-widest mt-0.5 ${isDarkMode ? 'text-white/30' : 'text-slate-400'}`}>
                                            {hospitalName || 'Hospital Status'}
                                        </p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => setIsOpen(false)}
                                    className={`p-2 rounded-xl transition-colors ${isDarkMode ? 'hover:bg-white/5 text-white/40' : 'hover:bg-slate-100 text-slate-400'}`}
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>

                            {sent ? (
                                /* Success State */
                                <div className="px-6 py-12 text-center shrink-0">
                                    <motion.div
                                        initial={{ scale: 0 }}
                                        animate={{ scale: 1 }}
                                        transition={{ type: 'spring', damping: 12 }}
                                        className="w-16 h-16 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-4"
                                    >
                                        <CheckCircle2 className="w-7 h-7 text-emerald-500" />
                                    </motion.div>
                                    <p className={`text-lg font-black ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                                        ¡Recibido!
                                    </p>
                                    <p className={`text-sm mt-2 ${isDarkMode ? 'text-white/40' : 'text-slate-500'}`}>
                                        Gracias. El equipo técnico lo revisará pronto.
                                    </p>
                                </div>
                            ) : (
                                /* Form Container (Scrollable if needed) */
                                <div className="flex-1 overflow-y-auto p-6 space-y-5 scrollbar-hide">
                                    
                                    {/* Category Selector */}
                                    <div>
                                        <label className={`block text-[10px] font-black uppercase tracking-[0.15em] mb-2 ${isDarkMode ? 'text-white/30' : 'text-slate-400'}`}>
                                            ¿De qué se trata?
                                        </label>
                                        <div className="grid grid-cols-2 gap-2">
                                            {CATEGORY_OPTIONS.map(opt => (
                                                <button
                                                    key={opt.value}
                                                    onClick={() => setCategory(opt.value)}
                                                    className={`flex items-center gap-2 p-2.5 rounded-xl text-[11px] font-bold uppercase tracking-wider transition-all border ${
                                                        category === opt.value
                                                            ? isDarkMode ? 'bg-indigo-500/20 border-indigo-500/50 text-indigo-400' : 'bg-indigo-50 border-indigo-200 text-indigo-600'
                                                            : isDarkMode ? 'bg-white/5 border-transparent text-white/40 hover:bg-white/10' : 'bg-slate-50 border-transparent text-slate-500 hover:bg-slate-100'
                                                    }`}
                                                >
                                                    <opt.icon className="w-4 h-4 flex-shrink-0" />
                                                    <span className="truncate">{opt.label}</span>
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Title */}
                                    <div>
                                        <label className={`block text-[10px] font-black uppercase tracking-[0.15em] mb-2 ${isDarkMode ? 'text-white/30' : 'text-slate-400'}`}>
                                            Asunto Breve *
                                        </label>
                                        <input
                                            type="text"
                                            value={title}
                                            onChange={(e) => setTitle(e.target.value)}
                                            placeholder="Ej: Error al agregar un cuarto"
                                            maxLength={200}
                                            autoFocus
                                            className={`w-full px-4 py-3 rounded-xl text-sm font-medium border-2 border-transparent transition-all outline-none ${
                                                isDarkMode
                                                    ? 'bg-white/5 focus:border-indigo-500 focus:bg-white/[0.08] text-white placeholder:text-white/20'
                                                    : 'bg-slate-50 focus:border-indigo-500 focus:bg-white text-slate-900 placeholder:text-slate-300'
                                            }`}
                                        />
                                    </div>

                                    {/* Description */}
                                    <div>
                                        <label className={`block text-[10px] font-black uppercase tracking-[0.15em] mb-2 ${isDarkMode ? 'text-white/30' : 'text-slate-400'}`}>
                                            Detalles (opcional)
                                        </label>
                                        <textarea
                                            value={description}
                                            onChange={(e) => setDescription(e.target.value)}
                                            placeholder="Describe lo que hacías, qué esperabas y qué pasó..."
                                            rows={3}
                                            maxLength={2000}
                                            className={`w-full px-4 py-3 rounded-xl text-sm font-medium border-2 border-transparent transition-all outline-none resize-none ${
                                                isDarkMode
                                                    ? 'bg-white/5 focus:border-indigo-500 focus:bg-white/[0.08] text-white placeholder:text-white/20'
                                                    : 'bg-slate-50 focus:border-indigo-500 focus:bg-white text-slate-900 placeholder:text-slate-300'
                                            }`}
                                        />
                                    </div>

                                    {/* Screenshot Feature */}
                                    <div>
                                        <label className={`block text-[10px] font-black uppercase tracking-[0.15em] mb-2 ${isDarkMode ? 'text-white/30' : 'text-slate-400'}`}>
                                            Evidencia Visual
                                        </label>
                                        {screenshotUrl ? (
                                            <div className="relative group rounded-xl overflow-hidden border border-slate-200">
                                                <img src={screenshotUrl} alt="Screenshot" className="w-full h-24 object-cover opacity-80 group-hover:opacity-100 transition-opacity" />
                                                <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                                    <button onClick={() => setScreenshotUrl(null)} className="px-3 py-1.5 bg-red-500 text-white rounded-lg text-xs font-bold shadow-lg flex items-center gap-1.5 hover:bg-red-600 transition-colors">
                                                        <X className="w-3.5 h-3.5" /> Quitar captura
                                                    </button>
                                                </div>
                                                <div className="absolute top-2 left-2 px-2 py-1 bg-emerald-500/90 backdrop-blur text-white rounded text-[10px] font-bold tracking-wider uppercase flex items-center gap-1">
                                                    <CheckCircle2 className="w-3 h-3" /> Capturado
                                                </div>
                                            </div>
                                        ) : (
                                            <button
                                                onClick={handleCapture}
                                                disabled={isCapturing}
                                                className={`w-full py-3 px-4 rounded-xl text-sm font-bold flex items-center justify-center gap-2 transition-all border border-dashed ${
                                                    isDarkMode 
                                                        ? 'border-white/20 text-white/60 hover:bg-white/5 hover:text-white' 
                                                        : 'border-slate-300 text-slate-500 hover:bg-slate-50 hover:text-slate-800'
                                                }`}
                                            >
                                                {isCapturing ? (
                                                    <>
                                                        <div className="w-4 h-4 border-2 border-indigo-500/30 border-t-indigo-500 rounded-full animate-spin" />
                                                        <span>Capturando pantalla...</span>
                                                    </>
                                                ) : (
                                                    <>
                                                        <Camera className="w-4 h-4" />
                                                        <span>Tomar captura automática de esta pantalla</span>
                                                    </>
                                                )}
                                            </button>
                                        )}
                                    </div>

                                    {/* Severity Selector */}
                                    <div>
                                        <label className={`block text-[10px] font-black uppercase tracking-[0.15em] mb-2 ${isDarkMode ? 'text-white/30' : 'text-slate-400'}`}>
                                            Severidad
                                        </label>
                                        <div className="flex gap-2">
                                            {SEVERITY_OPTIONS.map(opt => (
                                                <button
                                                    key={opt.value}
                                                    onClick={() => setSeverity(opt.value)}
                                                    className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-[11px] font-bold uppercase tracking-wider border transition-all ${
                                                        severity === opt.value
                                                            ? opt.bgActive
                                                            : `${opt.bg} ${isDarkMode ? 'text-white/60' : 'text-slate-500'}`
                                                    }`}
                                                >
                                                    <opt.icon className="w-3.5 h-3.5" />
                                                    {opt.label}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Submit */}
                                    <div className="pt-2">
                                        <motion.button
                                            whileHover={{ scale: 1.02 }}
                                            whileTap={{ scale: 0.98 }}
                                            onClick={handleSubmit}
                                            disabled={!title.trim() || title.trim().length < 3 || isSending || isCapturing}
                                            className={`w-full py-3.5 rounded-xl text-[11px] font-black uppercase tracking-[0.2em] transition-all flex items-center justify-center gap-2 ${
                                                !title.trim() || title.trim().length < 3 || isCapturing
                                                    ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
                                                    : 'bg-indigo-500 text-white hover:bg-indigo-600 shadow-lg shadow-indigo-500/20'
                                            }`}
                                        >
                                            {isSending ? (
                                                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                            ) : (
                                                <>
                                                    <Send className="w-3.5 h-3.5" />
                                                    Enviar al equipo
                                                </>
                                            )}
                                        </motion.button>
                                    </div>
                                </div>
                            )}
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default BugReportWidget;
